// =============================================================================
// SIREPPAR - Modal "Informe al Secretario Electoral" (B2)
// =============================================================================
// Genera el informe al Secretario (con o sin objeción) para expedientes de
// RECONOCIMIENTO, DESPUÉS de publicar los atributos (fase ATRIBUTOS_PUBLICADOS).
//  - "Generar borrador": genera y descarga el PDF sin persistir (regenerable).
//  - "Guardar informe": genera, descarga y guarda el informe como Documento
//    interno del expediente (queda en "Otros documentos", auto-verificado).
//    NO cambia el estado del expediente.
// =============================================================================
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import RichTextEditor from './RichTextEditor';
import { api } from '../../services/api';
import PanelHistorialInforme from './PanelHistorialInforme';
import { LABELS_ATRIBUTOS as LABELS, aplicarObjetados } from '../../utils/objecionAtributos';
import { aplicarAntecedentes, ItemHistorialInforme } from '../../utils/historialInforme';
import { aplicarVerificacionDocumental } from '../../utils/verificacionDocumental';
import { conEncabezadoInforme } from '../../utils/informeEncabezado';
import AdjuntosInforme from './AdjuntosInforme';

interface Props {
  // Bloque de advertencia sobre documentación obligatoria rechazada o faltante.
  // Se agrega al final del cuerpo (bloque gestionado) para que la supervisora
  // pueda informarlo aunque el trámite avance. '' = no hay nada que informar.
  advertenciaVerificacion?: string;
  // Denominación del partido: va al encabezado EDITABLE del cuerpo (el PDF ya no
  // la imprime fija, así se puede corregir desde el modal).
  nombrePartido?: string;
  isOpen: boolean;
  onClose: () => void;
  expedienteId: string;
  atributosDisponibles: string[];
  // Fecha límite del plazo de oposición (ISO). Si está presente, se muestra en
  // grande los días que faltan para que termine (útil para testing).
  fechaLimiteOposicion?: string;
  // Se llama tras guardar el informe (persistir): el contenedor refresca la vista.
  onGuardado?: () => void;
}

// Texto formal precargado (editable) según haya o no objeción. El PDF ya imprime
// el encabezado/destinatario; acá va solo el cuerpo. Admite Markdown (negrita/cursiva).
const TEXTO_SIN_OBJECION = `Se informa al Señor Secretario Electoral que se efectuó la publicación de la reserva de atributos de la agrupación en trámite de reconocimiento y que, vencido el plazo legal de cinco (5) días corridos, **no se presentaron objeciones ni observaciones** por parte de este Registro.

En virtud de lo expuesto, corresponde continuar con el trámite conforme a derecho.`;

const TEXTO_CON_OBJECION = `Se informa al Señor Secretario Electoral que, en relación a la publicación de la reserva de atributos de la agrupación en trámite de reconocimiento, **se presentó objeción** durante el plazo legal, según se detalla a continuación.

En virtud de lo expuesto, corresponde dejar constancia de la objeción formulada, la cual será oportunamente analizada conforme a la normativa vigente.`;

export default function ModalInformeSecretario({
  isOpen,
  onClose,
  expedienteId,
  atributosDisponibles,
  fechaLimiteOposicion,
  onGuardado,
  advertenciaVerificacion,
  nombrePartido,
}: Props) {
  const [conObjecion, setConObjecion] = useState(false);
  const [texto, setTexto] = useState(TEXTO_SIN_OBJECION);
  // Si el usuario editó el texto, no lo pisamos al cambiar de "sin/con objeción".
  const [textoTocado, setTextoTocado] = useState(false);

  const cambiarObjecion = (valor: boolean) => {
    setConObjecion(valor);
    if (!valor) {
      // Sin objeción: se limpian los atributos marcados y se quita el bloque del cuerpo.
      setAtributosEnConflicto([]);
      setTexto((c) => (textoTocado ? aplicarObjetados([], c) : conAdvertencia(TEXTO_SIN_OBJECION)));
    } else if (!textoTocado) {
      setTexto(conAdvertencia(TEXTO_CON_OBJECION));
    }
  };

  // Días que faltan para que termine el plazo de oposición (redondeo hacia arriba).
  const diasRestantesOposicion = (() => {
    if (!fechaLimiteOposicion) return null;
    const limite = new Date(fechaLimiteOposicion).getTime();
    if (Number.isNaN(limite)) return null;
    return Math.ceil((limite - Date.now()) / (1000 * 60 * 60 * 24));
  })();
  const [atributosEnConflicto, setAtributosEnConflicto] = useState<string[]>([]);
  const [generando, setGenerando] = useState(false);
  // Respaldo de la objeción: captura de pantalla, correo, nota escaneada. Se anexan
  // como hojas al final del PDF del informe (los PDF además van al correo).
  const [adjuntos, setAdjuntos] = useState<File[]>([]);

  // La advertencia es un bloque gestionado: se reaplica cada vez que se reemplaza
  // el cuerpo (al cambiar sin/con objeción se pisa el texto entero), así no se
  // pierde. Es idempotente.
  const conAdvertencia = (texto: string) =>
    conEncabezadoInforme(
      aplicarVerificacionDocumental(texto, advertenciaVerificacion ?? ''),
      { destinatario: 'SECRETARIO', nombrePartido },
    );

  // Antecedentes tildados en el panel lateral: se vuelcan como bloque al final
  // del cuerpo, sin pisar el bloque de atributos objetados ni lo redactado.
  const aplicarSeleccionHistorial = (items: ItemHistorialInforme[]) => {
    setTexto((cuerpo) => aplicarAntecedentes(items, cuerpo));
  };

  // Al abrir (o si la advertencia llega después de las queries) se refleja en el cuerpo.
  useEffect(() => {
    if (!isOpen) return;
    setTexto((cuerpo) => conAdvertencia(cuerpo));
  }, [isOpen, advertenciaVerificacion]);

  // Al cerrar/abrir se limpian los adjuntos: son de ESTE informe, no del siguiente.
  useEffect(() => {
    if (!isOpen) setAdjuntos([]);
  }, [isOpen]);

  const descargarPdf = (pdfBase64: string, filename: string) => {
    const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'informe-secretario.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generar = async (persistir: boolean) => {
    if (!texto.trim()) {
      toast.error('Redactá el cuerpo del informe');
      return;
    }
    if (conObjecion && atributosEnConflicto.length === 0) {
      toast.error('Marcá al menos un atributo objetado');
      return;
    }
    if (persistir && conObjecion) {
      if (!window.confirm('¿Registrar la objeción? Quedará asentada en el expediente y no se puede deshacer desde acá.')) {
        return;
      }
    }
    setGenerando(true);
    try {
      // Multipart: el endpoint acepta adjuntos de respaldo.
      const fd = new FormData();
      fd.append('conObjecion', String(conObjecion));
      fd.append('texto', texto.trim());
      fd.append('persistir', String(persistir));
      if (conObjecion) atributosEnConflicto.forEach((a) => fd.append('atributosEnConflicto', a));
      adjuntos.forEach((f) => fd.append('adjuntos', f));
      const res = await api.post(`/reconocimiento/${expedienteId}/informe-secretario`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      descargarPdf(res.data.data.pdfBase64, res.data.data.filename);
      toast.success(persistir ? (conObjecion ? 'Objeción registrada e informe guardado' : 'Informe guardado en el expediente') : 'Borrador generado');
      if (persistir) {
        onGuardado?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al generar el informe');
    } finally {
      setGenerando(false);
    }
  };

  const toggleAtributo = (attr: string) => {
    setAtributosEnConflicto((prev) => {
      const yaEsta = prev.includes(attr);
      let next: string[];
      if (attr === 'DENOMINACION' && !yaEsta) {
        next = Array.from(new Set([...prev, ...atributosDisponibles]));
      } else {
        next = yaEsta ? prev.filter((a) => a !== attr) : [...prev, attr];
      }
      // Ordenar según el orden de atributosDisponibles para un texto estable.
      const ordenados = atributosDisponibles.filter((a) => next.includes(a));
      // Reflejar la selección en el cuerpo del informe (bloque gestionado al final).
      setTexto((cuerpo) => aplicarObjetados(ordenados, cuerpo));
      return ordenados;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Informe al Secretario Electoral" size="full">
      <div className="space-y-5">
        {diasRestantesOposicion !== null && (
          diasRestantesOposicion > 0 ? (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-center">
              <p className="text-4xl font-extrabold text-red-600 leading-none">{diasRestantesOposicion}</p>
              <p className="text-sm font-semibold text-red-700 mt-1">
                {diasRestantesOposicion === 1 ? 'día' : 'días'} para que termine el plazo de oposiciones
              </p>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-green-300 bg-green-50 p-3 text-center">
              <p className="text-sm font-semibold text-green-700">El plazo de oposiciones finalizó.</p>
            </div>
          )
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => cambiarObjecion(false)}
            className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
              !conObjecion ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Sin objeción
          </button>
          <button
            type="button"
            onClick={() => cambiarObjecion(true)}
            className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
              conObjecion ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Con objeción
          </button>
        </div>

        {conObjecion && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Atributos objetados
              </label>
              <div className="space-y-2">
                {atributosDisponibles.map((attr) => (
                  <label key={attr} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={atributosEnConflicto.includes(attr)}
                      onChange={() => toggleAtributo(attr)}
                      className="w-4 h-4"
                    />
                    {LABELS[attr] || attr}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Al marcar atributos se agregan al final del cuerpo del informe. La redacción de la
                objeción va en el cuerpo.
              </p>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cuerpo del informe
            </label>
            <RichTextEditor
              value={texto}
              onChange={(v) => { setTexto(v ?? ''); setTextoTocado(true); }}
              minHeight={280}
              placeholder="Redacte el informe al Secretario Electoral. Puede dar formato al texto (negrita, cursiva, listas)…"
            />
            <p className="text-xs text-gray-400 mt-1">
              El PDF lista siempre todos los atributos del partido y adjunta los símbolos, más allá de este texto.
            </p>
          </div>
          {isOpen && (
            <PanelHistorialInforme
              expedienteId={expedienteId}
              resetKey={isOpen}
              onSeleccion={aplicarSeleccionHistorial}
            />
          )}
        </div>

        <AdjuntosInforme
          archivos={adjuntos}
          onChange={setAdjuntos}
          destacado={conObjecion}
          ayuda={conObjecion
            ? 'Adjuntá lo que justifica la objeción: captura de pantalla, el correo recibido o la nota escaneada en PDF.'
            : undefined}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => generar(false)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Generando…' : '📄 Generar borrador'}
          </button>
          <button
            type="button"
            onClick={() => generar(true)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Procesando…' : (conObjecion ? '✅ Registrar objeción y guardar' : '✅ Guardar informe')}
          </button>        </div>
      </div>
    </Modal>
  );
}
